/**
 * @fileoverview SessionViewerContext - Custom Hooks
 * 
 * Provides hooks for accessing the SessionViewerContext.
 * 
 * @module vtc/contexts/SessionViewerContext/hooks
 */

import { useContext } from 'react';
import { SessionViewerContext } from './SessionViewerContext';

/**
 * Hook to access the SessionViewerContext
 * 
 * Provides access to react-reverse-portal nodes for rendering SessionViewers
 * and functions to manage portal nodes. Must be used within a SessionViewerProvider.
 * 
 * @returns {Object} Context value
 * @returns {React.MutableRefObject} return.portalNodesRef - Map of sessionSid to portal nodes
 * @returns {Function} return.getOrCreatePortalNode - Function to get/create portal node for a session
 * @returns {Function} return.removeSessionViewer - Function to remove a session viewer's portal node
 * @throws {Error} If used outside SessionViewerProvider
 * 
 * @example
 * function MyComponent() {
 *   const { getOrCreatePortalNode, removeSessionViewer } = useSessionViewers();
 *   
 *   // Get portal node for a session
 *   const portalNode = getOrCreatePortalNode('session_123');
 *   
 *   // Use in OutPortal
 *   <portals.OutPortal node={portalNode} />
 *   
 *   // Remove portal node when done
 *   removeSessionViewer('session_123');
 * }
 */
export function useSessionViewers() {
    const context = useContext(SessionViewerContext);
    if (!context) {
        throw new Error('useSessionViewers must be used within SessionViewerProvider');
    }
    return context;
}
